/*
 * ## ⭐⭐ 「정본 그대로 이관」에서 ★ 이 파일은 ⇒ ⭐ «뺀다» (2026-10-10 · 관제 결정)
 *
 * ⛔ 덮으면 ★ ⭐ «두 번» 깨진다 — ⇒ 둘 다 실측:
 *
 *   ① ★ 2026-10-07 ⭐ «사용자 결정 ⓑ»를 ★ 되돌린다
 *      정본 ⇒ tProject(`'project.detail.back'`) · (`'project.detail.back_aria'`)
 *      우리 ⇒ tProject(`'project.home.back_home'`) · (`'project.home.back_home_aria'`)
 *      ⇒ ⇒ ★ 바로 아래 머리말이 ⭐ 그 결정을 적고 있다(지우지 말 것).
 *
 *   ② ⛔ 그리고 ★ 그 두 열쇠가 ⇒ ⭐ «우리 사전에 없다»
 *      정본 lib/project-i18n.ts:154-155 ⇒ ★ 선언이 ⭐ 있다
 *      우리 lib/project-i18n.ts:134     ⇒ ★ 그 자리가 ⭐ «그 결정을 적은 주석»이다(선언 0)
 *      ⇒ ⇒ ⛔ 즉 덮으면 ⇒ ★ tProject 가 ⭐ «열쇠 글자»를 돌려주고(:887 if (!m) return key)
 *         ⇒ ⇒ ★ 화면에 ⭐ 「project.detail.back」이 ★ 그대로 뜬다 — ⇒ ⛔ tsc 는 ★ 안 잡는다
 *            (ProjectI18nKey 가 ⭐ 사실상 string 이다 — ⇒ 탐침으로 확인했다 · 2026-10-10)
 *
 *   ③ ⭐ 그리고 ★ 더 센 까닭 ⇒ ⇒ 정본의 그 ko 값은 ★ ⭐ «거짓 안내»다
 *      ⇒ 그 이름의 화면이 ★ 2026-07-25 이후 ⭐ «없다»(그날 /project 가 「프로젝트 홈」이 되었다)
 *      ⇒ ⇒ ★ 즉 ⭐ 「그대로」가 ★ 사용자에게 ⭐ «더 나쁜» 자리다.
 *
 * ⏳ 만료 조건 — ★ ⭐ «둘이 함께» 참이 되는 날 (하나만으로는 ⛔ 부족하다)
 *   ⇒ ① 우리 lib/project-i18n 이 ★ `project.detail.back` · `back_aria` «라는 이름»을 ⭐ 내놓고
 *   ⇒ ② ★ 사용자가 ⭐ 그 옛 이름을 ★ 되살리기로 정하는 날
 *   ⇒ ⇒ ⛔ ① 만 되면 ★ 결정 ⓑ 를 어기고 ⇒ ② 만 되면 ⭐ 열쇠 글자가 뜬다.
 *   ⇒ ★ 그 까닭과 대조군은 ⭐ project-home-link-words.test.ts 머리말에 있다.
 */
import type { ReactNode } from 'react';
import { useSyncExternalStore } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from '../../icons';
// 쪽 머리 한 꼴(2026-10-17 · web 레인 W7d) — 제목 · 액션은 정본 머리가 진다
import { PageHeader } from '../../components/shared';
import { tProject, getProjectLang, subscribeProjectLang } from '../../lib/project-i18n';

interface DetailPageHeaderProps {
  title: string;
  /** 우측 액션 영역 (버튼 등) — 없으면 미표시. */
  actions?: ReactNode;
}

/**
 * DetailPageHeader (project-menu v2 드릴다운) — 상세 화면 **다섯** 공통 헤더
 * (teams · projects · my-tasks · stats · search).
 * ← 프로젝트 홈 back 링크 + 제목 + 우측 액션 slot.
 *   ⇒ 2026-10-17 사용자 결정 ②(web 레인 W7d): 그 링크가 ★ 정본 경로 줄의 첫 칸이 된다 —
 *      «프로젝트 홈(링크 → /project) › 제목». 글자 · 열쇠는 결정 ⓑ 그대로(`project.home.back_home`).
 *      경로 줄은 PageHeader 경로 줄과 «같은 마크업»이다(DetailPageHeader.crumbs.test 가 outerHTML 로 대조).
 *      ★ `<Link to="/project">` 를 이 파일에 그 꼴 그대로 둔다 — project-home-link-words.test 가 그 닻을 잰다.
 * 설계: docs/design/architecture-project-menu.md §3.3
 *
 * ## ⭐ 2026-10-07 사용자 결정 ⓑ — ★ 글자가 「대시보드」 ⇒ 「프로젝트 홈」
 *
 * ```
 * ⛔ 「대시보드」는 ★ 2026-07-25 이후 «아무 화면의 이름도 아니었다» —
 *    그날 /project 가 ⇒ 3c 「프로젝트 홈」이 되었고(그 전 이름이 「대시보드」),
 *    레일 메뉴 이름은 ⇒ 「프로젝트」다.
 * ```
 * ⚠ 값을 바꾸지 않고 ★ **이미 있던 열쇠를 쓴다**(`project.home.back_home`) —
 *   값만 바꾸면 ⇒ ⛔ 두 열쇠가 «같은 값»이 되어 ★ 다음 사람이 한쪽만 고친다.
 *   ⇒ 종전 `project.detail.back`·`_aria` 는 ★ 쓰는 곳이 여기 «하나»였으므로 ⭐ 지웠다.
 * ⚠ 그래도 글자는 ★ «둘»로 남는다 — ⭐ 그것이 «의도»다(2차 메뉴 안은 「홈」).
 *   ⇒ 그 까닭과 만료 조건은 `project-home-link-words.test.ts` 머리말에 있다.
 */
export function DetailPageHeader({ title, actions }: DetailPageHeaderProps) {
  useSyncExternalStore(subscribeProjectLang, getProjectLang, getProjectLang);
  return (
    <>
      <nav className="wc-page-head__crumb" aria-label="현재 위치">
        <span className="wc-page-head__crumb-item">
          <Link to="/project" className="wc-page-head__crumb-link">{tProject('project.home.back_home')}</Link>
        </span>
        <span className="wc-page-head__crumb-item">
          <ChevronRight className="wc-page-head__crumb-sep" aria-hidden />
          <span aria-current="page">{title}</span>
        </span>
      </nav>
      <PageHeader title={title} actions={actions} />
    </>
  );
}
